import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import api from "utils/api";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import { computeRoutePdfLength } from "utils/computeRoutePdfLength";
import { isFormControlFocused } from "utils/isFormControlFocused";
import { useKeyAction } from "hooks/useKeyAction";
import { hasOpenModal } from "components/common/Modal/modalStack";
import {
  setNodePositionInRoute,
  insertNodeOnEdgeInRoute,
  deleteNodeFromRoute,
} from "../../utils/routeGeometryMutators";
import { cloneRouteGeometry } from "../../utils/cloneRouteGeometry";
import { areRouteGeometriesEqual } from "../../utils/areRouteGeometriesEqual";
import { useNodeDragging } from "../useRoutesCreationModeOrchestration/useNodeDragging";
import { CREATION_RGB } from "components/SmartSurveyor/ReviewPage/FloorPlanPanelNew/SvgOverlay/RoutesCreationLayer/constants";
import type {
  Edge,
  ScaledNode,
} from "../../../SmartSurveyorForRudolfFritz/utils/types";
import type { NodeDraggingState } from "../useRoutesCreationModeOrchestration/useNodeDragging/types";
import type {
  InsertNodeOnEdgeArgs,
  InsertNodeOnEdgeResult,
  SetNodePositionArgs,
} from "../../RoutesCreationModeV3/hooks/types";
import type {
  UseRoutePathEditingArgs,
  UseRoutePathEditingResult,
} from "./types";

interface PathEditingDraft {
  routeId: string;
  nodes: ScaledNode[];
  edges: Edge[];
}

export const useRoutePathEditing = ({
  floorPlanId,
  groupLabel,
  selectedRoute,
  pageMetersPerPdfPoint,
  isPathEditing,
  stopPathEditing,
  onUpdateRouteGeometry,
  floorPlanPanelHandlers,
}: UseRoutePathEditingArgs): UseRoutePathEditingResult => {
  const { t } = useTranslation();

  const [draft, setDraft] = useState<PathEditingDraft | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<number | null>(null);
  const [hoverPosition, setHoverPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const setNodePosition = useCallback(
    ({ routeIndex, nodeId, pos }: SetNodePositionArgs) => {
      if (routeIndex !== 0) return;
      setDraft((prev) => {
        if (!prev) return prev;
        const updated = setNodePositionInRoute({
          geometry: { nodes: prev.nodes, edges: prev.edges },
          nodeId,
          pos,
        });
        return {
          routeId: prev.routeId,
          nodes: updated.nodes,
          edges: updated.edges,
        };
      });
    },
    [],
  );

  const insertNodeOnEdge = useCallback(
    ({
      routeIndex,
      edgeIndex,
      pos,
    }: InsertNodeOnEdgeArgs): InsertNodeOnEdgeResult | null => {
      if (routeIndex !== 0 || !draft) return null;
      // Computed outside the state updater: the caller needs the new id right away to select and drag it.
      const insertResult = insertNodeOnEdgeInRoute({
        geometry: { nodes: draft.nodes, edges: draft.edges },
        edgeIndex,
        pos,
      });
      if (!insertResult) return null;
      setDraft({
        routeId: draft.routeId,
        nodes: insertResult.geometry.nodes,
        edges: insertResult.geometry.edges,
      });
      return { newNodeId: insertResult.newNodeId };
    },
    [draft],
  );

  const deleteSelectedNode = useCallback(() => {
    if (!draft || selectedNodeId === null) return;
    const updated = deleteNodeFromRoute({
      geometry: { nodes: draft.nodes, edges: draft.edges },
      nodeId: selectedNodeId,
    });
    // Two nodes or a junction cannot lose a node; the route and the selection stay as they are.
    if (!updated) return;
    setDraft({
      routeId: draft.routeId,
      nodes: updated.nodes,
      edges: updated.edges,
    });
    setSelectedNodeId(null);
  }, [draft, selectedNodeId]);

  const draggingState = useMemo<NodeDraggingState | null>(() => {
    if (!draft) return null;
    return {
      routes: [{ id: draft.routeId, nodes: draft.nodes }],
      hoverPosition,
      insertNodeOnEdge,
      setNodePosition,
      setSelectedNodeId,
    };
  }, [draft, hoverPosition, insertNodeOnEdge, setNodePosition]);

  const noopDraggingState = useMemo<NodeDraggingState>(
    () => ({
      routes: [],
      hoverPosition: null,
      insertNodeOnEdge: () => null,
      setNodePosition: () => {},
      setSelectedNodeId,
    }),
    [],
  );

  const { handleNodeMouseDown, handleSegmentMouseDown, applyDragPosition } =
    useNodeDragging({
      isEditingActive: !!draft,
      creationModeState: draggingState ?? noopDraggingState,
    });

  const pathEditingMeters = useMemo(() => {
    if (!draft) return 0;
    const polylineLength = computeRoutePdfLength({
      nodes: draft.nodes,
      edges: draft.edges,
    });
    return polylineLength * pageMetersPerPdfPoint;
  }, [draft, pageMetersPerPdfPoint]);

  const isPathEditingDirty = useMemo(() => {
    if (!draft || !selectedRoute) return false;
    if (draft.routeId !== selectedRoute.id) return false;
    return !areRouteGeometriesEqual({
      first: { nodes: draft.nodes, edges: draft.edges },
      second: { nodes: selectedRoute.nodes, edges: selectedRoute.edges },
    });
  }, [draft, selectedRoute]);

  const pathEditingOverlay = useMemo(() => {
    if (!draft) return null;
    return {
      routes: [
        {
          id: draft.routeId,
          nodes: draft.nodes.map((node) => ({
            id: node.id,
            x: node.x,
            y: node.y,
          })),
          edges: draft.edges.map((edge) => ({
            from: edge.from,
            to: edge.to,
          })),
          rgb: CREATION_RGB,
          isActive: true,
        },
      ],
      hoverPosition: null,
      isEditable: true,
      selectedNodeId,
      onNodeMouseDown: handleNodeMouseDown,
      onSegmentMouseDown: handleSegmentMouseDown,
    };
  }, [draft, selectedNodeId, handleNodeMouseDown, handleSegmentMouseDown]);

  const submitPathEditing = useCallback(async () => {
    if (
      !draft ||
      !selectedRoute ||
      !groupLabel ||
      floorPlanId === undefined ||
      !isPathEditingDirty
    ) {
      return;
    }
    if (pathEditingMeters <= 0) {
      toast.error(t("smartSurveyor.routesCreation.metersRequired"));
      return;
    }
    setIsSubmitting(true);
    try {
      const meters = pathEditingMeters;
      await api.updateRoute(parseInt(draft.routeId, 10), {
        nodes: draft.nodes,
        edges: draft.edges,
        distancePx: 0,
        meters,
      });
      if (selectedRoute.id === draft.routeId) {
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_ROUTE_PATH_EDITED, {
          metresBefore: selectedRoute.meters,
          metresAfter: meters,
        });
      }
      onUpdateRouteGeometry({
        floorPlanId,
        groupLabel,
        routeId: draft.routeId,
        nodes: draft.nodes,
        edges: draft.edges,
        meters,
      });
      const firstNode = draft.nodes[0];
      if (firstNode) {
        floorPlanPanelHandlers?.current?.zoomToObject({
          x: firstNode.x,
          y: firstNode.y,
        });
      }
      stopPathEditing();
    } catch {
      toast.error(t("smartSurveyor.routeInspector.pathEditingFailed"));
    } finally {
      setIsSubmitting(false);
    }
  }, [
    draft,
    selectedRoute,
    groupLabel,
    floorPlanId,
    isPathEditingDirty,
    pathEditingMeters,
    onUpdateRouteGeometry,
    floorPlanPanelHandlers,
    stopPathEditing,
    t,
  ]);

  const cancelPathEditing = useCallback(() => {
    stopPathEditing();
  }, [stopPathEditing]);

  const handlePathEditingMapMouseMove = useCallback(
    (pos: { x: number; y: number } | null) => {
      if (pos && applyDragPosition(pos)) return;
      setHoverPosition(pos);
    },
    [applyDragPosition],
  );

  const deleteSelectedNodeFromKeyboard = useCallback(
    (event: KeyboardEvent) => {
      // The group delete and rename modals stay reachable while editing; the key is theirs then.
      if (isFormControlFocused(event) || hasOpenModal()) return;
      deleteSelectedNode();
    },
    [deleteSelectedNode],
  );

  const isDeleteSelectedNodeDisabled =
    draft === null || selectedNodeId === null || isSubmitting;

  useKeyAction({
    code: "KeyD",
    callback: deleteSelectedNodeFromKeyboard,
    options: { isDisabled: isDeleteSelectedNodeDisabled },
  });

  useKeyAction({
    code: "Backspace",
    callback: deleteSelectedNodeFromKeyboard,
    options: { isDisabled: isDeleteSelectedNodeDisabled },
  });

  useKeyAction({
    code: "Delete",
    callback: deleteSelectedNodeFromKeyboard,
    options: { isDisabled: isDeleteSelectedNodeDisabled },
  });

  // Seed when editing starts; clear when it ends. Keyed on routeId so cache
  // updates that don't change the route identity (e.g. group rename) don't
  // clobber the draft.
  useEffect(() => {
    if (isPathEditing && selectedRoute) {
      const cloned = cloneRouteGeometry({
        nodes: selectedRoute.nodes,
        edges: selectedRoute.edges,
      });
      setDraft({ routeId: selectedRoute.id, ...cloned });
      setSelectedNodeId(null);
      setHoverPosition(null);
    } else {
      setDraft(null);
      setSelectedNodeId(null);
      setHoverPosition(null);
      setIsSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPathEditing, selectedRoute?.id]);

  return {
    pathEditingOverlay,
    isPathEditingDirty,
    isPathEditingSubmitting: isSubmitting,
    pathEditingMeters,
    submitPathEditing,
    cancelPathEditing,
    handlePathEditingMapMouseMove,
  };
};
